import React from "react"
import {
  prepareCampaignHtmlForDisplay,
  descriptionHasCtaButton,
  descriptionHasContent,
  descriptionHasInlineImage,
  CAMPAIGN_TEMPLATE_BILINGUAL_SPLIT,
} from "../../../utils/campaignEmailContent"

const BLOCK_TAG_RE =
  /^(P|H[1-6]|UL|OL|BLOCKQUOTE|PRE|TABLE|DIV|SECTION|ARTICLE|MAIN|HEADER|FOOTER|CENTER|HR)$/
const WRAPPER_TAG_RE = /^(DIV|SECTION|ARTICLE|CENTER|MAIN|HEADER|FOOTER)$/
const BLOCK_SELECTOR =
  "p, h1, h2, h3, h4, h5, h6, ul, ol, blockquote, pre, table, div, section, article, main"

const isBlankHtml = (el) =>
  !(el.textContent || "").replace(/\u00A0/g, " ").trim() &&
  !el.querySelector("img, a")

const escapeText = (text) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

// Splits editor HTML into blocks so English/Arabic can be paired row by row:
//  - nested wrappers (<main>, <div>…) are flattened down to real blocks, and a
//    wrapper's inline style is kept on the blocks inside it
//  - a run of inline nodes (text, <b>, <span style>, <a>, <br>) stays ONE block
//  - blank lines are folded into the block above, so spacing shows without
//    shifting the pairing
const collectBlocks = (parent, out, inheritedStyle = "") => {
  const wrap = (html) =>
    inheritedStyle ? `<div style="${inheritedStyle}">${html}</div>` : html
  const addSpacer = (html) => {
    if (out.length) out[out.length - 1] += html
  }

  let inline = []
  const flushInline = () => {
    if (!inline.length) return
    const holder = document.createElement("div")
    holder.innerHTML = inline.join("")
    inline = []
    if (isBlankHtml(holder)) addSpacer(holder.innerHTML)
    else out.push(wrap(holder.innerHTML.trim()))
  }

  Array.from(parent.childNodes).forEach((node) => {
    if (node.nodeType === 3) {
      inline.push(escapeText(node.textContent || ""))
      return
    }
    if (node.nodeType !== 1) return
    if (!BLOCK_TAG_RE.test(node.tagName)) {
      inline.push(node.outerHTML)
      return
    }
    flushInline()
    if (isBlankHtml(node)) {
      addSpacer(node.outerHTML)
    } else if (WRAPPER_TAG_RE.test(node.tagName) && node.querySelector(BLOCK_SELECTOR)) {
      const style = [inheritedStyle, node.getAttribute("style") || ""]
        .filter(Boolean)
        .join(";")
      collectBlocks(node, out, style)
    } else {
      out.push(wrap(node.outerHTML))
    }
  })
  flushInline()
}

const splitTopLevelBlocks = (html) => {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html")
  const out = []
  collectBlocks(doc.body, out)
  return out
}

const PreviewStep = ({ formData }) => {
  const hasEnglishDescription = descriptionHasContent(
    formData.descriptionEnglish,
  )
  const hasArabicDescription = descriptionHasContent(formData.descriptionArabic)

  const hasEnglishContent =
    Boolean(formData.greetingEnglish?.trim()) || hasEnglishDescription

  const hasArabicContent =
    Boolean(formData.greetingArabic?.trim()) || hasArabicDescription

  const isBilingualSplit =
    formData.template === CAMPAIGN_TEMPLATE_BILINGUAL_SPLIT

  const showLanguagePartition = hasEnglishContent && hasArabicContent

  const showEnglishLegacyButton =
    hasEnglishContent &&
    Boolean(formData.buttonText && formData.buttonLink) &&
    !descriptionHasCtaButton(formData.descriptionEnglish)

  const showArabicLegacyButton =
    hasArabicContent &&
    Boolean(formData.buttonText && formData.buttonLink) &&
    !descriptionHasCtaButton(formData.descriptionArabic)

  const englishSection = hasEnglishContent && (
    <div className="preview-language-section">
      {formData.greetingEnglish && (
        <div className="preview-greeting">{formData.greetingEnglish}</div>
      )}

      {hasEnglishDescription && (
        <div
          className="preview-description"
          dangerouslySetInnerHTML={{
            __html: prepareCampaignHtmlForDisplay(
              formData.descriptionEnglish || "",
            ),
          }}
        />
      )}

      {showEnglishLegacyButton && (
        <div className="preview-button-container">
          <a
            href={formData.buttonLink}
            target="_blank"
            rel="noopener noreferrer"
            className="preview-button"
          >
            {formData.buttonText}
          </a>
        </div>
      )}
    </div>
  )

  const arabicSection = hasArabicContent && (
    <div
      className={`preview-language-section${
        showLanguagePartition && !isBilingualSplit
          ? " preview-arabic-section"
          : ""
      }`}
    >
      {formData.greetingArabic && (
        <div className="preview-greeting-arabic" dir="rtl">
          {formData.greetingArabic}
        </div>
      )}

      {hasArabicDescription && (
        <div
          className="preview-description-arabic"
          dir="rtl"
          dangerouslySetInnerHTML={{
            __html: prepareCampaignHtmlForDisplay(
              formData.descriptionArabic || "",
            ),
          }}
        />
      )}

      {showArabicLegacyButton && (
        <div className="preview-button-container">
          <a
            href={formData.buttonLink}
            target="_blank"
            rel="noopener noreferrer"
            className="preview-button"
            dir="rtl"
          >
            {formData.buttonTextArabic || formData.buttonText}
          </a>
        </div>
      )}
    </div>
  )

  // Pair English/Arabic blocks by position so each pair renders as one aligned row.
  const splitRows = (() => {
    if (!isBilingualSplit) return []
    const en = splitTopLevelBlocks(
      prepareCampaignHtmlForDisplay(formData.descriptionEnglish || ""),
    )
    const ar = splitTopLevelBlocks(
      prepareCampaignHtmlForDisplay(formData.descriptionArabic || ""),
    )
    // Pair blocks from the top and from the bottom (headline, closing call to
    // action…). When the counts differ, whatever is left in the middle flows as
    // one row, so nothing gets stretched against a block it doesn't match.
    if (en.length === ar.length) return en.map((block, i) => ({ en: block, ar: ar[i] }))
    const shared = Math.min(en.length, ar.length)
    const head = Math.ceil((shared - 1) / 2)
    const tail = Math.max(shared - 1 - head, 0)
    const row = (e, a) => ({ en: e.join(""), ar: a.join("") })
    return [
      ...en.slice(0, head).map((block, i) => ({ en: block, ar: ar[i] })),
      row(en.slice(head, en.length - tail), ar.slice(head, ar.length - tail)),
      ...en.slice(en.length - tail).map((block, i) => ({
        en: block,
        ar: ar[ar.length - tail + i],
      })),
    ]
  })()

  return (
    <div className="preview-step-container">
      <div className="preview-content">
        <div className="preview-logo">
          <div className="preview-logo-text">
            <img src="/Campaign/zory-logo-bg-white.svg" alt="" />
          </div>
        </div>

        {isBilingualSplit && formData.headerImage && (
          <div className="preview-template-header">
            <img
              src={formData.headerImage}
              alt="Header"
              className="preview-template-header-img"
            />
          </div>
        )}

        {formData.image &&
          !descriptionHasInlineImage(formData.descriptionEnglish) &&
          !descriptionHasInlineImage(formData.descriptionArabic) && (
            <div className="preview-image-container">
              <img
                src={formData.image}
                alt="Campaign"
                className="preview-image"
              />
            </div>
          )}

        {isBilingualSplit ? (
          <>
            {(formData.greetingEnglish || formData.greetingArabic) && (
              <div className="preview-split-columns">
                <div className="preview-split-column">
                  <div className="preview-greeting">
                    {formData.greetingEnglish}
                  </div>
                </div>
                <div className="preview-split-divider" />
                <div className="preview-split-column">
                  <div className="preview-greeting-arabic" dir="rtl">
                    {formData.greetingArabic}
                  </div>
                </div>
              </div>
            )}
            {splitRows.map((row, i) => (
              <div className="preview-split-columns" key={i}>
                <div className="preview-split-column">
                  <div
                    className="preview-description"
                    dangerouslySetInnerHTML={{ __html: row.en }}
                  />
                </div>
                <div className="preview-split-divider" />
                <div className="preview-split-column">
                  <div
                    className="preview-description-arabic"
                    dir="rtl"
                    dangerouslySetInnerHTML={{ __html: row.ar }}
                  />
                </div>
              </div>
            ))}
            {(showEnglishLegacyButton || showArabicLegacyButton) && (
              <div className="preview-split-columns">
                <div className="preview-split-column">{showEnglishLegacyButton && (
                  <div className="preview-button-container">
                    <a href={formData.buttonLink} target="_blank" rel="noopener noreferrer" className="preview-button">
                      {formData.buttonText}
                    </a>
                  </div>
                )}</div>
                <div className="preview-split-divider" />
                <div className="preview-split-column">{showArabicLegacyButton && (
                  <div className="preview-button-container">
                    <a href={formData.buttonLink} target="_blank" rel="noopener noreferrer" className="preview-button" dir="rtl">
                      {formData.buttonTextArabic || formData.buttonText}
                    </a>
                  </div>
                )}</div>
              </div>
            )}
          </>
        ) : (
          <>
            {englishSection}
            {arabicSection}
          </>
        )}

        {isBilingualSplit && formData.footerImage && (
          <div className="preview-template-footer">
            <img
              src={formData.footerImage}
              alt="Footer"
              className="preview-template-footer-img"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default PreviewStep
